import React, { Component } from 'react'
import cx from 'classnames'
import PropTypes from 'prop-types'
import { DescriptionIcon } from '@wf-sl/common-icons'

import T3Constants from '../constants'
import Tooltip from '../tooltip/tooltip'

import styles from './card.module.css'

const propTypes = {
    bodyClassName: PropTypes.string,
    border: PropTypes.bool,
    children: PropTypes.any, // eslint-disable-line react/forbid-prop-types
    className: PropTypes.string,
    defaultText: PropTypes.string,
    icon: PropTypes.element,
    iconButton: PropTypes.element,
    iconClassName: PropTypes.string,
    label: PropTypes.string,
    labelClassName: PropTypes.string,
    labelTooltip: PropTypes.string,
    labelTooltipClassName: PropTypes.string,
    split: PropTypes.bool,
    splitClassName: PropTypes.string,
    splitLeftClassName: PropTypes.string,
    splitRightClassName: PropTypes.string,
    text: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.object]),
    textItalic: PropTypes.bool,
    valuesClassName: PropTypes.string
}

const defaultProps = {
    // all other properties can be undefined
    border: true,
    defaultText: T3Constants.HtmlEntities.MDASH,
    split: false,
    textItalic: false
}

/**
 * Render card element.
 *
 * @param {string} [props.bodyClassName] - Optional classname for the text body
 * @param {boolean} [props.border=true] - Whether to render borders below the element
 * @param {string} [props.className] - Optional classname for the card element
 * @param {string} [props.defaultText=MDASH] - Fallback if there are no children or text
 * @param {ReactElement} [props.children] - The jsx children to be rendered
 * @param {ReactElement} [props.icon] - Optional icon component displayed near text
 * @param {ReactElement} [props.iconButton] - Optional icon button component displayed near text
 * @param {string} [props.iconClassName] - Optional classname for icon component (not icon button)
 * @param {string} [props.label] - Label content
 * @param {string} [props.labelClassName] - Optional classname for the label element
 * @param {boolean} [props.split=false] - Whether to split the card content in 2 cols or not
 * @param {string} [props.splitClassName] - Optional classname for the split container
 * @param {string} [props.splitLeftClassName] - Optional classname for the split element on the left
 * @param {string} [props.splitRightClassName] - Optional classname for the split element on the right
 * @param {string|number|Object} [props.text] - The text content
 * @param {boolean} [props.textItalic=false] - Flag to set the text italic
 * @param {string} [props.valuesClassName] - Optional classname for the wrapper holding label/text/button
 * @returns {ReactElement}
 */
class CardElement extends Component {
    render() {
        return this.props.split ? this.renderSplitElement() : this.renderElement()
    }

    renderSplitElement() {
        const numberOfChildren = React.Children.count(this.props.children)
        const { splitClassName, splitLeftClassName, splitRightClassName } = this.props

        if (numberOfChildren === 2) {
            // renders a 2 row card for small and big cards
            return (
                <div className={cx(styles['card-element-split'], splitClassName)}>
                    <div className={cx(styles['card-element-split-left'], splitLeftClassName)}>
                        {this.renderElement(0)}
                    </div>
                    <div className={cx(styles['card-element-split-right'], splitRightClassName)}>
                        {this.renderElement(1)}
                    </div>
                </div>
            )
        } else if (numberOfChildren === 4) {
            // renders a 4 row card only available for big cards
            return (
                <div className={cx(styles['card-element-split'], splitClassName)}>
                    <div className={cx(styles['card-element-double-split-left'], splitLeftClassName)}>
                        {this.renderElement(0)}
                    </div>
                    <div className={cx(styles['card-element-double-split-second'], splitRightClassName)}>
                        {this.renderElement(1)}
                    </div>
                    <div className={cx(styles['card-element-double-split-left'], splitLeftClassName)}>
                        {this.renderElement(2)}
                    </div>
                    <div className={cx(styles['card-element-double-split-last'], splitRightClassName)}>
                        {this.renderElement(3)}
                    </div>
                </div>
            )
        } else {
            console.error('only 2 and 4 children can be applied to card-element-split')
            return null
        }
    }

    /**
     * Renders an single element.
     */
    renderElement(idx = -1) {
        // element
        //   -> icon
        //   -> values
        //     -> label
        //     -> text

        const numChildren = React.Children.count(this.props.children)
        if (idx > -1 && numChildren > 0 && idx < numChildren) {
            const children = React.Children.toArray(this.props.children)

            return children[idx]
        }

        const {
            bodyClassName,
            border,
            className,
            icon,
            iconButton,
            iconClassName,
            label,
            labelClassName,
            labelTooltip,
            labelTooltipClassName,
            textItalic,
            valuesClassName
        } = this.props

        const textClassName = cx(
            styles['card-element-body'],
            { [styles['card-element-body-italic']]: textItalic },
            bodyClassName,
            't3sel-value'
        )

        const valuesClass = cx(
            styles['card-element-values'],
            {
                [styles['card-element-values-no-border']]: !border,
                [styles['with-iconbutton']]: iconButton
            },
            valuesClassName,
            't3sel-card-element-values'
        )

        const labelDiv = labelTooltip ? (
            <Tooltip className={labelTooltipClassName} content={labelTooltip} placement="top">
                <div className={styles['card-element-label-tooltip']}>
                    {label}
                    <DescriptionIcon className={styles['description-icon']} />
                </div>
            </Tooltip>
        ) : (
            label
        )

        return (
            <div className={cx(styles['card-element'], className, 't3sel-card-element')}>
                <div className={cx(styles['card-element-icon'], iconClassName)}>{icon}</div>
                <div className={valuesClass}>
                    <div className={cx(styles['card-element-label'], labelClassName, 't3sel-label')}>{labelDiv}</div>
                    <div className={textClassName}>{this.getText()}</div>
                    <div className={styles['button-wrapper']}>{iconButton}</div>
                </div>
            </div>
        )
    }

    getText() {
        // text-attribute over child components, cause this component is intended to show text
        if (this.props.text) {
            return this.props.text
        }

        if (React.Children.count(this.props.children) > 0) {
            return this.props.children
        }

        return this.props.defaultText
    }
}

CardElement.propTypes = propTypes
CardElement.defaultProps = defaultProps

export { CardElement }
